if (!customElements.get('bike-select')) {
  class BikeSelect extends HTMLElement {
    connectedCallback() {
      this.nativeSelect = this.querySelector('select');
      if (!this.nativeSelect) return;

      this.trigger = this.buildTrigger();
      this.listbox = this.buildListbox();

      this.prepend(this.trigger);
      this.appendChild(this.listbox);

      this.updateDisplay();

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };

      this.trigger.addEventListener('click', () => this.toggle());
      this.addEventListener('keydown', (event) => this.handleKeydown(event));
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    buildTrigger() {
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'bike-select__trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');

      const display = document.createElement('span');
      display.className = 'bike-select__display';

      this.swatchEl = document.createElement('span');
      this.swatchEl.className = 'bike-select__swatch';
      this.swatchEl.hidden = true;

      this.valueEl = document.createElement('span');
      this.valueEl.className = 'bike-select__value';

      display.append(this.swatchEl, this.valueEl);

      const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      chevron.setAttribute('class', 'bike-select__chevron');
      chevron.setAttribute('viewBox', '0 0 12 8');
      chevron.setAttribute('width', '12');
      chevron.setAttribute('height', '8');
      chevron.innerHTML =
        '<path d="M1 1.5L6 6.5L11 1.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>';

      trigger.append(display, chevron);
      return trigger;
    }

    buildListbox() {
      const listbox = document.createElement('ul');
      listbox.className = 'bike-select__listbox';
      listbox.setAttribute('role', 'listbox');
      listbox.hidden = true;

      let hasSwatches = false;

      Array.from(this.nativeSelect.options).forEach((option, index) => {
        const li = document.createElement('li');
        li.className = 'bike-select__option';
        li.setAttribute('role', 'option');
        li.dataset.index = String(index);
        li.setAttribute('aria-selected', option.selected ? 'true' : 'false');
        if (option.disabled) li.classList.add('is-disabled');
        if (option.selected) li.classList.add('is-selected');

        const swatchValue = option.dataset.optionSwatchValue;
        if (swatchValue) {
          hasSwatches = true;
          const swatch = document.createElement('span');
          swatch.className = 'bike-select__swatch';
          swatch.style.background = swatchValue;
          if (swatchValue.startsWith('url(')) {
            swatch.style.backgroundSize = 'cover';
            swatch.style.backgroundPosition = 'center';
          }
          li.appendChild(swatch);
        }

        const label = document.createElement('span');
        label.textContent = option.textContent.trim();
        li.appendChild(label);

        li.addEventListener('click', () => this.selectOption(index));
        listbox.appendChild(li);
      });

      this.classList.toggle('has-swatch', hasSwatches);
      return listbox;
    }

    get isOpen() {
      return !this.listbox.hidden;
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      document.querySelectorAll('bike-select.is-open').forEach((el) => {
        if (el !== this) el.close();
      });
      this.listbox.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      this.positionListbox();
    }

    positionListbox() {
      const GAP = 6;
      const MIN_HEIGHT = 120;
      const MAX_HEIGHT = 260;
      const EDGE = 12;

      this.listbox.scrollTop = 0;
      // Clear inline sizing from a previous open before measuring.
      this.listbox.style.width = '';
      this.listbox.style.left = '';
      this.listbox.style.right = '';

      const triggerRect = this.trigger.getBoundingClientRect();

      // A label like "Bottom Bracket - Shimano Spec BB (+$129.00 AUD)" wraps to
      // three lines inside a half-width mobile column, so let the panel be wider
      // than its trigger -- then pull it back left if that would push it past the
      // right edge of the viewport. Never narrower than the trigger.
      this.listbox.style.right = 'auto';
      this.listbox.style.width = 'max-content';
      const natural = this.listbox.getBoundingClientRect().width;
      const width = Math.min(
        Math.max(natural, triggerRect.width),
        window.innerWidth - EDGE * 2
      );
      this.listbox.style.width = `${width}px`;

      const overflowRight = triggerRect.left + width - (window.innerWidth - EDGE);
      if (overflowRight > 0) {
        const shift = Math.min(overflowRight, Math.max(0, triggerRect.left - EDGE));
        if (shift > 0) this.listbox.style.left = `${-shift}px`;
      }

      // Overlaying the fields below is expected for a select; only flip up when
      // the list would otherwise run off the bottom of the viewport.
      const roomBelow = window.innerHeight - triggerRect.bottom - GAP;
      const roomAbove = triggerRect.top - GAP;
      const dropUp = roomBelow < MIN_HEIGHT && roomAbove > roomBelow;

      this.classList.toggle('drop-up', dropUp);
      const room = dropUp ? roomAbove : roomBelow;
      this.listbox.style.maxHeight = `${Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, room))}px`;
    }

    close() {
      this.listbox.hidden = true;
      this.listbox.style.maxHeight = '';
      this.listbox.style.width = '';
      this.listbox.style.left = '';
      this.listbox.style.right = '';
      this.trigger.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      this.classList.remove('drop-up');
    }

    selectOption(index) {
      const option = this.nativeSelect.options[index];
      if (!option || option.disabled) return;

      this.nativeSelect.selectedIndex = index;
      this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));

      this.listbox.querySelectorAll('.bike-select__option').forEach((li, i) => {
        li.classList.toggle('is-selected', i === index);
        li.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

      this.updateDisplay();
      this.close();
      this.trigger.focus();
    }

    updateDisplay() {
      const selected = this.nativeSelect.options[this.nativeSelect.selectedIndex];
      if (!selected) return;

      this.valueEl.textContent = selected.textContent.trim();

      const swatchValue = selected.dataset.optionSwatchValue;
      if (swatchValue) {
        this.swatchEl.style.background = swatchValue;
        if (swatchValue.startsWith('url(')) {
          this.swatchEl.style.backgroundSize = 'cover';
          this.swatchEl.style.backgroundPosition = 'center';
        } else {
          this.swatchEl.style.backgroundSize = '';
          this.swatchEl.style.backgroundPosition = '';
        }
        this.swatchEl.hidden = false;
      } else {
        this.swatchEl.hidden = true;
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        this.trigger.focus();
        return;
      }
      if ((event.key === 'Enter' || event.key === ' ') && document.activeElement === this.trigger) {
        event.preventDefault();
        this.toggle();
      }
    }
  }

  customElements.define('bike-select', BikeSelect);
}

// The size guide link next to the Size picker jumps to the "Geometry & Size
// Guide" collapsible further down the same page (expanding it if collapsed).
if (!window.bikeSizeGuideJumpBound) {
  window.bikeSizeGuideJumpBound = true;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-jump-sizeguide]');
    if (!trigger) return;

    const tab = document.querySelector('.size-guidecust');
    if (!tab) return;

    event.preventDefault();

    if (typeof tab.open === 'function' && !tab.selected) {
      tab.open();
    }

    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--m-header-height'), 10);
    const offset = Number.isNaN(headerHeight) ? 20 : headerHeight + 20;
    const top = tab.getBoundingClientRect().top + window.pageYOffset - offset;

    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  });
}
